import { Check, Phone, ArrowRight } from "lucide-react";
import { CallLink } from "./CallLink";
import planBasicAsset from "@/assets/plan-basic.png.asset.json";
import planBonusAsset from "@/assets/plan-bonus.png.asset.json";
import planDhamakaNewAsset from "@/assets/plan-dhamaka-new.png.asset.json";

const planBasic = planBasicAsset.url;
const planBonus = planBonusAsset.url;
const planDhamaka = planDhamakaNewAsset.url;

type Plan = {
  image: string;
  badge: string;
  title: string;
  subtitle: string;
  price: string;
  tagline: string;
  features: string[];
  highlight?: boolean;
  ribbon?: string;
};

const plans: Plan[] = [
  {
    image: planBasic,
    badge: "Basic Switch",
    title: "Tata Play HD Box Without Antenna",
    subtitle: "Use your existing antenna",
    price: "₹1,999",
    tagline: "Get started with Tata Play — basic setup without extra add-ons.",
    features: [
      "HD Set-Top Box Included",
      "No Antenna Required (Non ODU)",
      "Free Professional Installation",
      "Pay only for the HD Box and setup",
      "*Monthly rental charges extra",
    ],
  },
  {
    image: planBonus,
    badge: "Best Value",
    title: "Pay ₹3,100 — Get ₹3,600",
    subtitle: "Use your existing antenna",
    price: "₹3,100",
    tagline: "Save time and money — keep your current antenna and still get the works.",
    features: [
      "Free Tata Play HD Set-Top Box",
      "Free Installation by Certified Technicians",
      "₹600 Bonus Recharge",
      "No Extra Cost — Use Your Current Dish (Non-ODU)",
      "Cost-effective, fast switch to superior entertainment",
    ],
    highlight: true,
    ribbon: "Most Popular",
  },
  {
    image: planDhamaka,
    badge: "Dhamaka Offer",
    title: "Pay ₹3,600 — Get ₹3,600 Back",
    subtitle: "100% cashback to your account",
    price: "₹3,600",
    tagline: "Pay ₹3,600 and get the full value credited back to your Tata Play account.",
    features: [
      "100% Cashback — ₹3,600 credited to your account",
      "12 Months ₹300 Value Pack (Worth ₹3,600)",
      "Free Installation by Certified Technicians",
      "Free HD Set-Top Box",
      "Use the amount to select your favourite packs",
    ],
  },
];

export function Plans() {
  return (
    <section className="bg-background py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
            Pricing
          </span>
          <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
            Choose Your <span className="text-accent">Perfect Plan</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Flexible plans designed to fit every budget and entertainment need.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => (
            <PlanCard key={p.title} plan={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan }: { plan: Plan }) {
  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-3xl border bg-card text-card-foreground transition hover:-translate-y-1 ${
        plan.highlight
          ? "border-accent shadow-[var(--shadow-glow)]"
          : "border-border shadow-sm hover:shadow-[var(--shadow-elevated)]"
      }`}
    >
      {plan.ribbon && (
        <span className="absolute right-4 top-4 z-10 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow">
          {plan.ribbon}
        </span>
      )}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={plan.image}
          alt={plan.title}
          loading="lazy"
          width={800}
          height={600}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <span className="self-start rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
          {plan.badge}
        </span>
        <h3 className="mt-3 text-xl font-bold">{plan.title}</h3>
        <p className="text-sm text-muted-foreground">{plan.subtitle}</p>

        <div className="mt-4 flex items-baseline gap-2">
          <span className="text-4xl font-black tracking-tight text-accent">{plan.price}</span>
          <span className="text-sm text-muted-foreground">onwards</span>
        </div>
        <p className="mt-2 text-sm text-foreground/80">{plan.tagline}</p>

        <ul className="mt-5 space-y-2.5">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-success/15 text-success">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              <span className="text-foreground/90">{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <CallLink
            location="plans_card"
            label={`Plan Card - ${plan.title}`}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground shadow-[var(--shadow-glow)] transition hover:brightness-110"
          >
            <Phone className="h-4 w-4" /> Call Now
          </CallLink>
          <a
            href="#form"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground transition hover:bg-secondary"
          >
            Book Now <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </article>
  );
}
